/* istanbul ignore file */
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import React, { PureComponent } from 'react';
import get from 'lodash/get';
import find from 'lodash/find';
import fetch from 'isomorphic-fetch';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Loader from 'yoda-core-components/lib/components/Loader/Loader';
import {
    detectisIE,
    detectisEdge,
    findMobileOS,
} from 'yoda-core-components/lib/helpers/Utils/Utils';
import ScriptLoader from 'yoda-core-components/lib/helpers/ScriptLoader/ScriptLoader';
import { selectPreferences, selectContext } from '../../selectors/ContextSelector';
import { parseQueryString } from '../../helpers/Utils/interactiveVizNav';
import * as styles from './Scene7Player.css';

const cx = classNames.bind(styles);

/*
 * Created a new fetch helper for scene7 requests
 * RestClient.get always parses the data and we do not want to parse for these requests
 */

export const fetchHelper = (url) =>
    fetch(url).then((res) => res.text().then((text) => ({ status: res.status, text })));

const localConfig = {
    scene7VideoViewerURL: 'https://jcpenney.scene7.com/s7viewers/html5/js/VideoViewer.js',
    autoPlayConfig: 'jcpenneyimages/JCPenney_Auto_Video',
    config: 'jcpenneyimages/JCPenney_Video',
    params: {
        serverurl: 'https://jcpenney.scene7.com/is/image/',
        contenturl: 'https://jcpenney.scene7.com/is/content/',
        emailurl: 'https://jcpenney.scene7.com/s7/emailFriend',
        videoserverurl: 'https://jcpenney.scene7.com/is/content/',
    },
    adobeVideoPlaceholder:
        'https://www.static-jcpenney.com/prod3/yoda-home/assets/svg/video-error.svg',
};
const initiatedVideoContainer = [];
export class Scene7Player extends PureComponent {
    static propTypes = {
        defaultScene7VideoSettings: PropTypes.shape({
            params: PropTypes.shape({
                serverurl: PropTypes.string.isRequired,
                contenturl: PropTypes.string.isRequired,
            }),
            config: PropTypes.string.isRequired,
            autoPlayConfig: PropTypes.string.isRequired,
            scene7VideoViewerURL: PropTypes.string.isRequired,
            adobeVideoPlaceholder: PropTypes.string.isRequired,
            containerHeightForIE: PropTypes.string,
            triggerVideoPlayTimeout: PropTypes.number,
            cycleResetTimeout: PropTypes.number,
            extraSpace: PropTypes.number,
        }).isRequired,
        videoId: PropTypes.string.isRequired,
        index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        onEnd: PropTypes.func,
        disableAutoPlay: PropTypes.bool,
        automationId: PropTypes.string,
        url: PropTypes.string,
        isResponsive: PropTypes.bool.isRequired,
        isDesktop: PropTypes.bool.isRequired,
        selectedIndex: PropTypes.number,
        isPDP: PropTypes.bool,
        videoTheme: PropTypes.string,
        isMobile: PropTypes.bool.isRequired,
        setVideoLoaded: PropTypes.func.isRequired,
        loop: PropTypes.bool,
    };

    static defaultProps = {
        index: 0,
        onEnd: () => {},
        disableAutoPlay: false,
        automationId: '',
        url: '',
        selectedIndex: 0,
        isPDP: false,
        videoTheme: '',
        loop: false,
    };

    constructor() {
        super();
        this.state = {
            videoStarted: false,
            hasError: false,
            slideNumber: 0,
        };
        this.videoExits = this.videoExits.bind(this);
        this.addListeners = this.addListeners.bind(this);
        this.removeListeners = this.removeListeners.bind(this);
        this.touchStartListener = this.touchStartListener.bind(this);
        this.touchEndListener = this.touchEndListener.bind(this);
        this.playPauseListener = this.playPauseListener.bind(this);
        this.___SCROLL_TOP_LAST___ = 0;
        this.isPlaying = true;
        this.timer = null;
    }

    componentDidMount() {
        const {
            defaultScene7VideoSettings: { scene7VideoViewerURL },
        } = this.props;
        const { s7viewers: { VideoViewer } = {} } = window;
        if (!VideoViewer) {
            ScriptLoader.load({
                src: scene7VideoViewerURL,
                async: true,
                defer: false,
                onSuccess: this.onLoadSuccess.bind(this),
            });
        } else {
            this.onVideoScriptLoad();
            this.videoExits();
        }
    }

    componentWillUnmount() {
        const { isDesktop } = this.props;
        /* Handle the double tap issue YODATOF-393 please do not modify anything in below listeners */
        if (!isDesktop) {
            this.removeListeners();
        }
    }

    onLoadSuccess = () => {
        this.onVideoScriptLoad();
        this.videoExits();
    };

    onCanPlay = () => {
        const {
            index,
            videoId,
            defaultScene7VideoSettings: { containerHeightForIE = '480px', extraSpace = 15 },
            isResponsive,
            isDesktop,
        } = this.props;
        this.playVideo();
        if ((detectisIE() || detectisEdge()) && isResponsive) {
            if (document.getElementById(`scene7-${videoId}-${index}_container`)) {
                document.getElementById(
                    `scene7-${videoId}-${index}_container`
                ).style.height = containerHeightForIE;
            }
        }
        if (document.getElementById(`scene7-${videoId}-${index}`) && isResponsive) {
            document.getElementById(
                `scene7-${videoId}-${index}`
            ).style.height = document
                .getElementById(`scene7-${videoId}-${index}`)
                .querySelector('video').style.height;
            if ((detectisIE() || detectisEdge()) && isResponsive) {
                if (document.getElementById(`${videoId}-${index}_container`)) {
                    document.getElementById(
                        `${videoId}-${index}_container`
                    ).style.height = document
                        .getElementById(`${videoId}-${index}`)
                        .querySelector('video').style.height;
                }
            }
        }

        /*
         * please do not modify anything in below listeners
         * manually trigger click event for playing the video when tapped on screen
         * YODATOF-393
         */
        if (!isDesktop) {
            this.addListeners();
        }

        if (window.innerWidth <= 360) {
            setTimeout(() => {
                if (document.getElementById(`scene7-${videoId}-${index}_controls`)) {
                    const { width } = document.getElementById(
                        `scene7-${videoId}-${index}_controls`
                    ).style;
                    const splitWidth = width.split('px')[0];
                    const newWidth = parseInt(splitWidth, 10) + extraSpace;
                    document.getElementById(
                        `scene7-${videoId}-${index}_controls`
                    ).style.width = `${newWidth}px`;
                }
            }, 300);
        }
    };

    onError = () => {
        const { disableAutoPlay, onEnd } = this.props;
        !disableAutoPlay && this.playVideo();
        setTimeout(() => {
            // Showing the error to user for sometime
            onEnd();
        }, 2000);
        this.setState({ hasError: true });
    };

    onVideoScriptLoad = async () => {
        const {
            videoId,
            onEnd,
            disableAutoPlay,
            defaultScene7VideoSettings: { params, config, autoPlayConfig },
            url,
            index,
            isPDP,
            isMobile,
            loop,
        } = this.props;
        let urlParams = {};
        let closedCaptioningParam = {};
        if (url) {
            urlParams = parseQueryString(url);
        } else if (window.innerWidth > 360) {
            closedCaptioningParam = await this.captioningExits();
        }

        const videoViewerParams = {
            containerId: `scene7-${videoId}-${index}`,
            params: {
                asset: `jcpenneyimages/${videoId}-AVS`,
                posterimage: `jcpenneyimages/${videoId}-AVS`,
                ...closedCaptioningParam,
                ...params,
                ...urlParams,
                config: disableAutoPlay ? config : autoPlayConfig,
                config2: 'companypreset',
                loop: loop ? 'true' : 'false',
            },
            handlers: {
                initComplete: () => this.onCanPlay(),
                trackEvent: (objID, compClass, instName, timeStamp, eventInfo) => {
                    if (eventInfo.split(',')[0] === 'STOP') {
                        onEnd();
                    }
                },
            },
        };

        if (window.s7viewers && window.s7viewers.VideoViewer) {
            if (isPDP && isMobile) {
                const containerInstance = find(
                    initiatedVideoContainer,
                    (o) => o === videoViewerParams.containerId
                );
                new window.s7viewers.VideoViewer(videoViewerParams).init();
                !containerInstance && initiatedVideoContainer.push(videoViewerParams.containerId);
            } else {
                new window.s7viewers.VideoViewer(videoViewerParams).init();
            }
        }
    };

    playVideo = () => {
        const { setVideoLoaded } = this.props;
        this.setState({
            videoStarted: true,
        });
        setVideoLoaded &&
            setVideoLoaded({
                isVideoLoaded: true,
            });
    };

    pauseOnSlideChange = (isPlaying) => {
        const { videoId, index, selectedIndex } = this.props;
        if (this.videoRef && isPlaying && selectedIndex !== this.state.slideNumber) {
            const videoDOM =
                this.videoRef.ownerDocument &&
                this.videoRef.ownerDocument
                    .getElementById(`scene7-${videoId}-${index}`)
                    .querySelector('video');
            videoDOM && videoDOM.pause();
            this.setState({
                slideNumber: selectedIndex,
            });
        }
    };

    playPauseListener(isPlaying) {
        const { isPDP, isDesktop } = this.props;
        this.isPlaying = isPlaying;
        isPDP && !isDesktop && this.pauseOnSlideChange(isPlaying);
    }

    touchEndListener() {
        const {
            videoId,
            index,
            defaultScene7VideoSettings: { triggerVideoPlayTimeout = 300, cycleResetTimeout = 2000 },
            isPDP,
        } = this.props;
        const scrollPosition = window.scrollY || window.pageYOffset;
        /* STEP 2 wait to see if video was played from adobe end */
        setTimeout(() => {
            /* STEP 3. do not trigger click during scroll */
            if (scrollPosition === this.___SCROLL_TOP_LAST___) {
                /* STEP 4. trigger click to play video is video was paused. */
                if (
                    document.getElementById(`scene7-${videoId}-${index}`).querySelector('video')
                        .paused &&
                    this.isPlaying &&
                    !isPDP
                ) {
                    document
                        .getElementById(`scene7-${videoId}-${index}`)
                        .querySelector('video')
                        .play();
                }
                /* STEP 5. trigger click to replay video when video play is completed. */
                if (
                    document
                        .getElementById(`scene7-${videoId}-${index}`)
                        .querySelector('[state="replay"]') &&
                    findMobileOS() === 'iOS'
                ) {
                    document
                        .getElementById(`scene7-${videoId}-${index}`)
                        .querySelector('video')
                        .play();
                }
            }
        }, triggerVideoPlayTimeout);

        /* STEP 6 wait for the next set of events. reset the cycle of triggering events manually */
        this.timer = setTimeout(() => {
            if (
                document.getElementById(`scene7-${videoId}-${index}`).querySelector('video').paused
            ) {
                this.isPlaying = true;
            }
        }, cycleResetTimeout);
    }

    touchStartListener(event) {
        const { videoId, index, isPDP } = this.props;
        if (isPDP) {
            const VideoElem = document.getElementById(`scene7-${videoId}-${index}_container`);
            const getMode = VideoElem?.getAttribute('mode');
            const isFullScreen = getMode === 'fullscreen';
            isFullScreen && event.stopPropagation();
        }
        /* STEP 1. set scroll position to not trigger click on scroll events */
        const scrollPosition = window.scrollY || window.pageYOffset;
        this.___SCROLL_TOP_LAST___ = scrollPosition;
        /* clear if any timer was set while waiting */
        clearTimeout(this.timer);
    }

    addListeners() {
        const videoTagRef = this.videoRef?.querySelector('video');
        if (this.videoRef) {
            this.videoRef.addEventListener('touchstart', this.touchStartListener);
            this.videoRef.addEventListener('touchend', this.touchEndListener);
        }
        if (videoTagRef) {
            videoTagRef.addEventListener('play', () => this.playPauseListener(true));
            videoTagRef.addEventListener('pause', () => this.playPauseListener(false));
        }
    }

    removeListeners() {
        const videoTagRef = this.videoRef?.querySelector('video');
        if (this.videoRef) {
            this.videoRef.removeEventListener('touchstart', this.touchStartListener);
            this.videoRef.removeEventListener('touchend', this.touchEndListener);
        }
        if (videoTagRef) {
            videoTagRef.removeEventListener('play', () => this.playPauseListener(true));
            videoTagRef.removeEventListener('pause', () => this.playPauseListener(false));
        }
    }

    /* make a call to server to see if video exists
     * returns exists=1 if video exists
     * returns exists=0 if video does not exists
     * hide the video player when video player does not exists.
     */
    async videoExits() {
        const {
            defaultScene7VideoSettings: {
                params: { serverurl },
            },
            videoId = '',
        } = this.props;
        const { status, text = '' } = await fetchHelper(
            `${serverurl}/jcpenneyimages/${videoId}-AVS?req=exists`
        );
        if (
            (status === 200 && text.includes('exists=0')) ||
            videoId.toString().includes('youtube') ||
            status !== 200
        ) {
            this.setState({ videoStarted: true }, () => this.onError());
        }
    }

    /* make a call to content to see if closed captioning exists
     * returns exists=1 if CC exists
     * returns exists=0 if CC does not exists
     */
    async captioningExits() {
        const {
            defaultScene7VideoSettings: {
                params: { contenturl },
            },
            videoId = '',
        } = this.props;
        const { status, text = '' } = await fetchHelper(
            `${contenturl}/jcpenneyimages/videos/tt/${videoId}.vtt?req=exists`
        );
        if (status === 200 && text.includes('exists=1')) {
            return { caption: `jcpenneyimages/videos/tt/${videoId}.vtt` };
        }
        return {};
    }

    render() {
        const {
            videoId,
            index,
            disableAutoPlay,
            automationId,
            defaultScene7VideoSettings: {
                adobeVideoPlaceholder = 'http://holder.ninja/400x300.svg',
            },
            isResponsive,
            isPDP,
            isDesktop,
            videoTheme,
            componentType,
        } = this.props;
        const { videoStarted, hasError } = this.state;
        const videoAutomationId = automationId || `prod-video-${index}`;
        const videoThemeStyle =
            componentType === 'JCPStoryVideo' || componentType === 'JCPStoryVideoEdgeToEdge'
                ? videoTheme
                : '';
        return (
            <div className={cx(dt(['flex', 'h-full', 'w-full', 'relative']), `${videoThemeStyle}`)}>
                {((!videoStarted && !disableAutoPlay) || (isResponsive && !videoStarted)) && (
                    <Loader
                        automationId="test-automation-loader-1"
                        loaderClass={dt([
                            'bg-black',
                            'h-[calc(100%-10px)]',
                            'h-full',
                            'w-full',
                            'my-0',
                            'mx-auto',
                            'z-[2]',
                        ])}
                    />
                )}
                {hasError ? (
                    <img
                        src={adobeVideoPlaceholder}
                        className={dt(['my-0', 'mx-auto', 'w-full', 'h-full'])}
                        data-automation-id={videoAutomationId}
                        alt={`${videoId}-adobe-video-failed-to-load`}
                    />
                ) : (
                    <div
                        className={dt([isResponsive && 'h-[400px]', isResponsive && 'smOnly:h-64'])}
                        id={`scene7-${videoId}-${index}`}
                        data-automation-id={videoAutomationId}
                        ref={(node) => {
                            this.videoRef = node;
                        }}
                    />
                )}
                {isPDP && !isDesktop && this.pauseOnSlideChange(this.isPlaying)}
            </div>
        );
    }
}

export const mapStateToProps = (state) => ({
    defaultScene7VideoSettings: get(
        selectPreferences(state),
        'defaultScene7VideoSettings',
        localConfig
    ),
    isDesktop: get(selectContext(state), 'deviceType.isDesktop'),
    isMobile: get(selectContext(state), 'deviceType.isMobile'),
});

export default connect(mapStateToProps)(Scene7Player);
